/**
 * AnonProvisioningFailed — anon free-trial provisioning failure state.
 *
 * Companion to AnonTrialExhausted, but for a DIFFERENT server outcome: any
 * provisionAnonKey() failure that is NOT the IP-cap exhaustion codes
 * (sandbox_limit_exceeded / upgrade_limit_exceeded / HTTP 429) — e.g. a
 * network blip, a transient provisioning error, or a cold start. Previously
 * this case silently fell through to firing the real stream/upload request
 * with a null token, which the server rejected with a raw "Authentication
 * failed — please sign in again, then retry." error deep in the product UI
 * (V2StreamOverlay.classifyStreamError) — a dead-end, not an invitation.
 *
 * This surface is deliberately an inviting retry prompt (transient failure,
 * try again) rather than an auth error (which implies the user did something
 * wrong). Signing in is offered as an alternative, not the only path out.
 *
 * App-plane component (brand asset — logo rule: Darwin_v3_vinyl_transparent.png).
 */
import { L } from '../../core/components/v2/pv2-shared'

export function AnonProvisioningFailed({
  onRetry,
  onDismiss,
}: {
  onRetry?: () => void
  onDismiss?: () => void
}) {
  return (
    <div
      data-testid="anon-provisioning-failed"
      style={{
        position: 'fixed', inset: 0, zIndex: 9000,
        background: 'rgba(26,25,22,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{
        maxWidth: 420, width: '100%', background: L.bgSurface,
        border: `1px solid ${L.border}`, borderRadius: 16,
        padding: '40px 32px', textAlign: 'center',
        boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 24 }}>
          <img src="/Darwin_v3_vinyl_transparent.png" alt="" style={{ height: 32, width: 'auto', objectFit: 'contain' }} />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, textAlign: 'left' }}>
            <span style={{ fontSize: 15, fontWeight: 300, color: L.textPrimary, letterSpacing: '-0.025em' }}>Primate</span>
            <span style={{ fontSize: 15, fontWeight: 300, color: L.signal, letterSpacing: '-0.025em' }}>Intelligence</span>
          </div>
        </div>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: L.textPrimary, margin: '0 0 12px' }}>
          Couldn&rsquo;t start your free trial
        </h2>
        <p style={{ fontSize: 15, color: L.textMuted, lineHeight: 1.6, margin: '0 0 24px' }}>
          Check your connection and try again, or sign in if you already have
          an account.
        </p>
        {onRetry && (
          <button
            data-testid="anon-provisioning-failed-retry"
            onClick={onRetry}
            style={{
              display: 'inline-block', background: L.signal, color: '#fff',
              fontSize: 15, fontWeight: 500, padding: '12px 28px',
              borderRadius: 10, border: 'none', cursor: 'pointer',
            }}
          >
            Try again
          </button>
        )}
        <div style={{ marginTop: 16 }}>
          <a href="/login" style={{ fontSize: 13, color: L.textMuted, textDecoration: 'underline' }}>
            Already have an account? Sign in
          </a>
        </div>
        {onDismiss && (
          <div style={{ marginTop: 12 }}>
            <button
              data-testid="anon-provisioning-failed-dismiss"
              onClick={onDismiss}
              style={{ fontSize: 12, color: L.textMuted, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
            >
              Not now
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
